import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ForumIcon from '@mui/icons-material/Forum';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';

import { StatusBadge, ModalChatBody, ConfirmModal } from './index';
import { supplierQuotes, supplierMessages, purchaseOrders, quoteHistory, supplierRatings } from '../../services/dataClient';
import Rating from '@mui/material/Rating';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Field style (shared across both sub-forms) ───────────────────────────────

// ─── Enter Quote Response sub-modal ──────────────────────────────────────────
function EnterQuoteResponseModal({ open, quote, onClose, onSubmit }) {
  const [unitCost, setUnitCost] = useState('');
  const [qtyReq,   setQtyReq]   = useState('1');
  const [notes,    setNotes]    = useState('');

  const isEdit = quote?.unitCost != null;

  useEffect(() => {
    if (open) {
      setUnitCost(quote?.unitCost != null ? String(quote.unitCost) : '');
      setQtyReq(String(quote?.quantityRequested ?? 1));
      setNotes(quote?.notes ?? '');
    }
  }, [open, quote]);

  const canSave = unitCost !== '' && qtyReq !== '' && parseInt(qtyReq, 10) >= 1;

  const handleSave = () => {
    onSubmit({
      quoteId:           quote.id,
      unitCost:          parseFloat(unitCost),
      quantityRequested: Math.max(1, parseInt(qtyReq, 10) || 1),
      quantity:          Math.max(1, parseInt(qtyReq, 10) || 1),
      notes:             notes.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            {isEdit ? 'Edit Quote Response' : 'Enter Quote Response'}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '3px', fontWeight: 400 }}>
            {quote?.supplierName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, flexShrink: 0 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important', pb: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: '4px' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField size="small" label="Unit Cost ($)" required type="number" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} inputProps={{ min: 0, step: '0.01' }} sx={{ ...fieldSx, flex: 1 }} />
            <TextField size="small" label="Qty Requested" required type="number" value={qtyReq} onChange={(e) => setQtyReq(e.target.value)} inputProps={{ min: 1, step: 1 }} sx={{ ...fieldSx, flex: 1 }} />
          </Box>
          <TextField size="small" label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={2} fullWidth sx={fieldSx} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1 }}>
        <Typography onClick={onClose} sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' }, mr: 'auto' }}>
          Cancel
        </Typography>
        <Button
          variant="contained" size="small" disabled={!canSave} onClick={handleSave}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}
        >
          {isEdit ? 'Save Changes' : 'Save Response'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main SupplierQuotesModal ─────────────────────────────────────────────────
export default function SupplierQuotesModal({ open, order, onClose, onStatusChange }) {
  const [localQuotes,        setLocalQuotes]        = useState([]);
  const [supplierMsgMap,     setSupplierMsgMap]     = useState({});
  const [chatQuote,          setChatQuote]          = useState(null);
  const [responseModalQuote, setResponseModalQuote] = useState(null);
  const [acceptedPO,         setAcceptedPO]         = useState(null); // set when a quote is accepted; modal stays open
  const [confirmQuote,       setConfirmQuote]       = useState(null); // quote pending confirmation before accept
  const [historyOpen,        setHistoryOpen]        = useState(false);

  // Re-seed whenever the order changes
  useEffect(() => {
    if (!order) return;
    const quotes = supplierQuotes.filter((q) => q.partOrderId === order.id);
    setLocalQuotes(quotes);
    setAcceptedPO(null);
    setHistoryOpen(false);

    const map = {};
    const quoteIds = new Set(quotes.map((q) => q.id));
    supplierMessages.forEach((m) => {
      if (!quoteIds.has(m.quoteId)) return;
      if (!map[m.quoteId]) map[m.quoteId] = [];
      map[m.quoteId].push(m);
    });
    setSupplierMsgMap(map);
    // We only re-seed when the order identity changes, not on every prop-new reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Reset sub-modals when outer modal closes
  useEffect(() => {
    if (!open) {
      setChatQuote(null);
      setResponseModalQuote(null);
    }
  }, [open]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Close handler: notify parent of accepted PO (if any) before closing
  const handleClose = () => {
    if (acceptedPO) {
      onStatusChange('Fulfilled', acceptedPO);
    }
    onClose();
  };

  const handleAccept = (acceptedQuote) => {
    const today = dayjs().format('YYYY-MM-DD');
    const newPO = {
      id: `po-rfq-${Date.now()}`,
      poNumber: `PO-${6000 + purchaseOrders.length + 1}`,
      vendorName: acceptedQuote.supplierName,
      partNumber: order.partNumber,
      partDescription: order.partDescription ?? '',
      quantity: acceptedQuote.quantity ?? 1,
      unitCost: acceptedQuote.unitCost ?? 0,
      lineItems: [{
        partNumber: order.partNumber,
        partDescription: order.partDescription ?? '',
        quantity: acceptedQuote.quantity ?? 1,
        unitCost: acceptedQuote.unitCost ?? 0,
        lineTotal: (acceptedQuote.unitCost ?? 0) * (acceptedQuote.quantity ?? 1),
      }],
      totalCost: (acceptedQuote.unitCost ?? 0) * (acceptedQuote.quantity ?? 1),
      expectedDeliveryDate: dayjs().add(acceptedQuote.leadTimeDays ?? 5, 'day').format('YYYY-MM-DD'),
      notes: `Accepted from RFQ for ${order.ticketNumber}`,
      status: 'Submitted',
      date: today,
      linkedOrderId: order.id,
      linkedTicketNumber: order.ticketNumber,
    };
    purchaseOrders.unshift(newPO);

    // Mutate module-level supplierQuotes so landing page reflects the decision
    supplierQuotes.forEach((q) => {
      if (q.partOrderId !== order.id) return;
      if (q.id === acceptedQuote.id) q.status = 'Accepted';
      else if (q.status === 'Received') q.status = 'Declined';
    });

    setLocalQuotes((prev) =>
      prev.map((q) => ({
        ...q,
        status: q.id === acceptedQuote.id ? 'Accepted' : (q.status === 'Received' ? 'Declined' : q.status),
      }))
    );
    setAcceptedPO(newPO);
    // Modal stays open — parent is notified when user closes
  };

  const handleEnterResponse = ({ quoteId, unitCost, quantity, quantityRequested, notes }) => {
    const alreadyAccepted = localQuotes.some((q) => q.status === 'Accepted');
    const newStatus = alreadyAccepted ? 'Declined' : 'Received';
    setLocalQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId
          ? { ...q, status: newStatus, unitCost, quantity, quantityRequested, notes: notes || null }
          : q
      )
    );
    // Also persist the declined status to the module-level array
    if (alreadyAccepted) {
      const target = supplierQuotes.find((q) => q.id === quoteId);
      if (target) target.status = 'Declined';
    }
    setResponseModalQuote(null);
  };

  const handleMsgSend = (text) => {
    if (!chatQuote) return;
    const msg = {
      id: `sm-${Date.now()}`,
      quoteId: chatQuote.id,
      sender: 'coordinator',
      senderName: 'You',
      text,
      sentAt: new Date().toISOString(),
    };
    setSupplierMsgMap((prev) => ({
      ...prev,
      [chatQuote.id]: [...(prev[chatQuote.id] ?? []), msg],
    }));
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const received    = localQuotes.filter((q) => q.status !== 'Pending').length;
  const total       = localQuotes.length;
  const orderStatus = order?.status ?? 'Open';
  const acceptedQuote = localQuotes.find((q) => q.status === 'Accepted') ?? null;

  const history = quoteHistory
    .filter((h) => h.partNumber === order?.partNumber)
    .sort((a, b) => (a.responseDate < b.responseDate ? 1 : -1));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '80vh' } }}
      >
        {/* Header */}
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
              Supplier Quotes — {order?.ticketNumber}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '6px' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
                {order?.partNumber}
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: '#E0F2F1' }}>
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#00695C' }}>
                  {received} / {total} responded
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, flexShrink: 0 }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>

        {/* Quote rows */}
        <DialogContent sx={{ p: 0, pt: '12px !important', overflowY: 'auto' }}>
          {/* Acceptance confirmation banner */}
          {acceptedPO && (
            <Box sx={{ mx: 3, mt: 2, mb: 1, px: 2, py: '12px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20, mt: '1px', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1B5E20' }}>
                  Quote accepted — {acceptedQuote?.supplierName}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#2E7D32', mt: '2px' }}>
                  PO {acceptedPO.poNumber} created · Est. delivery {acceptedPO.expectedDeliveryDate}
                </Typography>
              </Box>
            </Box>
          )}

          {localQuotes.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                No quotes found for this order.
              </Typography>
            </Box>
          ) : (
            localQuotes.map((q, i) => {
              const isPending  = q.status === 'Pending';
              const isAccepted = q.status === 'Accepted';
              const isDeclined = q.status === 'Declined';
              // Gray out rows that are not the accepted supplier (once one is accepted — current session or prior)
              const isGrayedOut = acceptedQuote && !isAccepted;
              const canAccept   = q.status === 'Received'
                && !acceptedQuote
                && orderStatus !== 'Fulfilled'
                && orderStatus !== 'Shipped'
                && orderStatus !== 'Completed';

              return (
                <Box key={q.id}>
                  {i > 0 && <Divider sx={{ borderColor: 'var(--grey-200)' }} />}
                  <Box sx={{ px: 3, py: '14px', opacity: isGrayedOut ? 0.45 : 1, transition: 'opacity 0.2s' }}>
                    {/* Row 1: name + email + badge + chat icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '6px' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                            {q.supplierName}
                          </Typography>
                          {isAccepted && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', px: '7px', py: '1px', borderRadius: '10px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7' }}>
                              <CheckCircleIcon sx={{ fontSize: 11, color: '#2E7D32' }} />
                              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#1B5E20' }}>Selected</Typography>
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                          {q.supplierEmail}
                        </Typography>
                        {(() => {
                          const ratings = supplierRatings.filter((r) => r.supplierName === q.supplierName);
                          if (!ratings.length) return null;
                          const avg = ratings.reduce((s, r) => s + r.rating, 0) / ratings.length;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
                              <Rating
                                value={avg}
                                readOnly
                                precision={0.5}
                                size="small"
                                sx={{ fontSize: '0.875rem', color: 'var(--warning-dark)', '& .MuiRating-iconEmpty': { color: 'var(--grey-300)' } }}
                              />
                              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)' }}>
                                ({ratings.length})
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <StatusBadge status={isAccepted ? 'Received' : isDeclined ? 'Cancelled' : q.status === 'Received' ? 'Received' : 'Pending'} />
                        <Tooltip title="Messages" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => setChatQuote(q)}
                            sx={{ color: (supplierMsgMap[q.id] ?? []).length > 0 ? '#009688' : 'var(--grey-300)', '&:hover': { color: '#009688', backgroundColor: '#E0F2F1' }, p: '4px' }}
                          >
                            <ForumIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {isPending ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                          Awaiting response…
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setResponseModalQuote(q)}
                          sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, color: '#009688', p: 0, minWidth: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                        >
                          Enter Response
                        </Button>
                      </Box>
                    ) : (
                      <>
                        {/* Row 2: cost / qty */}
                        <Box sx={{ display: 'flex', gap: 3, mb: '6px', flexWrap: 'wrap' }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unit Cost</Typography>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#009688' }}>
                              {q.unitCost != null ? `$${q.unitCost.toFixed(2)}` : '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qty</Typography>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--grey-800)' }}>
                              {q.quantityRequested ?? q.quantity ?? q.quantityAvailable ?? '—'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Row 3: notes + accept */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            {q.notes && (
                              <Tooltip title={q.notes} placement="top" arrow enterDelay={200}>
                                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {q.notes}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                          {canAccept && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                              <Typography
                                onClick={() => setResponseModalQuote(q)}
                                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                              >
                                Edit
                              </Typography>
                              <Button
                                size="small" variant="outlined" onClick={() => setConfirmQuote(q)}
                                sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, borderColor: '#009688', color: '#009688', '&:hover': { backgroundColor: '#e0f2f1', borderColor: '#009688' }, py: '3px', px: '12px' }}
                              >
                                Accept
                              </Button>
                            </Box>
                          )}
                          {isAccepted && (
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2E7D32', flexShrink: 0 }}>
                              ✓ Accepted
                            </Typography>
                          )}
                          {isDeclined && (
                            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', flexShrink: 0 }}>
                              Declined
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
          {/* Price History section */}
          {history.length > 0 && (
            <>
              <Divider sx={{ borderColor: 'var(--grey-200)' }} />
              {/* Section header — toggle */}
              <Box
                onClick={() => setHistoryOpen((p) => !p)}
                sx={{ px: 3, py: '11px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: historyOpen ? '#E0F2F1' : '#f0faf9',
                  '&:hover': { backgroundColor: '#E0F2F1' },
                  transition: 'background-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#00695C' }}>
                    Price History
                  </Typography>
                  <Box sx={{ px: '7px', py: '1px', borderRadius: '10px', backgroundColor: '#B2DFDB' }}>
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#00695C' }}>
                      {history.length} record{history.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#009688' }}>
                    — {order?.partNumber}
                  </Typography>
                </Box>
                {historyOpen
                  ? <ExpandLessIcon sx={{ fontSize: 17, color: '#009688' }} />
                  : <ExpandMoreIcon sx={{ fontSize: 17, color: '#009688' }} />}
              </Box>

              {historyOpen && (
                <Box sx={{ pb: 1 }}>
                  {/* Table header */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '95px 80px 1fr 72px 90px', px: 3, py: '6px', backgroundColor: 'var(--grey-100)', borderTop: '1px solid var(--grey-200)', borderBottom: '1px solid var(--grey-200)' }}>
                    {['Date', 'Ticket #', 'Supplier', 'Price', 'Status'].map((h) => (
                      <Typography key={h} sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--grey-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {h}
                      </Typography>
                    ))}
                  </Box>
                  {/* Rows */}
                  {history.map((h, i) => (
                    <Box
                      key={h.id}
                      sx={{ display: 'grid', gridTemplateColumns: '95px 80px 1fr 72px 90px', px: 3, alignItems: 'center', minHeight: 38, borderBottom: i < history.length - 1 ? '1px solid var(--grey-100)' : 'none' }}
                    >
                      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)' }}>
                        {dayjs(h.responseDate).format('MMM D, YYYY')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', fontFamily: 'monospace' }}>
                        {h.ticketNumber}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>
                        {h.supplierName}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#009688' }}>
                        ${h.unitCost.toFixed(2)}
                      </Typography>
                      {h.status === 'Accepted' ? (
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2E7D32' }}>✓ Accepted</Typography>
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>Declined</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept confirmation */}
      <ConfirmModal
        open={Boolean(confirmQuote)}
        title="Accept Quote"
        message={`Accept the quote from ${confirmQuote?.supplierName} at $${confirmQuote?.unitCost?.toFixed(2) ?? '—'} / unit? A purchase order will be created and other suppliers will be marked as declined.`}
        confirmLabel="Accept Quote"
        onConfirm={() => { handleAccept(confirmQuote); setConfirmQuote(null); }}
        onCancel={() => setConfirmQuote(null)}
      />

      {/* Enter Response sub-modal */}
      <EnterQuoteResponseModal
        open={Boolean(responseModalQuote)}
        quote={responseModalQuote}
        onClose={() => setResponseModalQuote(null)}
        onSubmit={handleEnterResponse}
      />

      {/* Supplier chat sub-dialog */}
      <Dialog
        open={Boolean(chatQuote)}
        onClose={(_, reason) => { if (reason !== 'backdropClick') setChatQuote(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
              Messages — {chatQuote?.supplierName}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px', fontWeight: 400 }}>
              {order?.ticketNumber} · Supplier correspondence
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setChatQuote(null)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, flexShrink: 0 }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, pt: '12px !important', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ModalChatBody
            messages={chatQuote ? (supplierMsgMap[chatQuote.id] ?? []) : []}
            onSend={handleMsgSend}
            outgoingSender="coordinator"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
